// src/pages/RfidMasterList.jsx
import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import { FaPencilAlt, FaTrashAlt, FaTimesCircle, FaTags, FaPlus, FaFilter } from "react-icons/fa";
import { FiRotateCcw } from "react-icons/fi";
import toast from "react-hot-toast";

import ConfirmDialog from "../components/ConfirmDialog";
import {
  useGetAllRfidMastersQuery,
  useDeleteRfidMasterMutation,
} from "../toolkitServices/rfidMasterApi";
import { useDebounce } from "../hooks/useDebounced";

/* ------------ Tiny reusable spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const headerCell =
  "px-2 sm:px-3 py-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide";
const dataCell = "px-2 sm:px-3 py-2 text-xs sm:text-sm text-gray-700";

const SkeletonRows = ({ cols = 7, rows = 8 }) => {
  const widths = ["w-10", "w-20", "w-24", "w-28", "w-16", "w-32"];

  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={`sk-${r}`} className="animate-pulse border-t">
          {Array.from({ length: cols }).map((__, c) => (
            <td key={`sk-${r}-${c}`} className={dataCell}>
              <div
                className={`h-3 ${widths[c % widths.length]} bg-slate-200 rounded`}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;

  return dt.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getRowId = (item) => item?._id || item?.id || item?.Id;
const getRfidCode = (item) =>
  item?.rfidCode ||
  item?.RFIDCode ||
  item?.code ||
  item?.Code ||
  item?.tagCode ||
  item?.TagCode ||
  "-";

const RfidMasterList = () => {
  const navigate = useNavigate();

  // filters + paging
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput.trim(), 400);
  const [isActive, setIsActive] = useState("");
  const [isAssigned, setIsAssigned] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({ isActive: "", isAssigned: "" });

  // confirm modal state
  const [confirm, setConfirm] = useState({
    open: false,
    payload: null,
  });

  const queryArgs = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch,
      isActive,
      isAssigned,
    }),
    [page, limit, debouncedSearch, isActive, isAssigned],
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetAllRfidMastersQuery(queryArgs);

  const [deleteRfidMaster, { isLoading: deletingRfid }] =
    useDeleteRfidMasterMutation();

  const rows =
    data?.data ||
    data?.items ||
    data?.results ||
    data?.rfidMasters ||
    data?.docs ||
    [];

  const pagination = data?.pagination || {};
  const totalDocs =
    pagination?.totalDocs ??
    data?.total ??
    data?.totalCount ??
    rows?.length ??
    0;

  const totalPages =
    pagination?.totalPages ??
    (totalDocs > 0 ? Math.ceil(totalDocs / limit) : 1);

  const hasPrev = pagination?.hasPrev ?? page > 1;
  const hasNext = pagination?.hasNext ?? page < totalPages;

  const handleReset = () => {
    setPage(1);
    setLimit(10);
    setSearchInput("");
    setIsActive("");
    setIsAssigned("");
  };

  const openFilters = () => {
    setTempFilters({ isActive, isAssigned });
    setIsFilterOpen(true);
  };

  const openConfirmDelete = (item) => {
    setConfirm({
      open: true,
      payload: {
        id: getRowId(item),
        label: getRfidCode(item),
      },
    });
  };

  const closeConfirm = () => {
    setConfirm({
      open: false,
      payload: null,
    });
  };

  const handleConfirmDelete = async () => {
    try {
      const id = confirm?.payload?.id;
      const label = confirm?.payload?.label || "record";

      if (!id) {
        toast.error("Invalid RFID master id");
        return;
      }

      await deleteRfidMaster(id).unwrap();
      toast.success(`Deleted ${label}`);
      closeConfirm();

      // If last item on page deleted, go previous page if possible
      if (rows.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
      }
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.data?.msg ||
        err?.error ||
        "Delete failed";
      toast.error(msg);
    }
  };

  const paginationSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 32,
      height: 32,
      borderRadius: 6,
      borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 1px rgba(148,129,206,.25)" : "none",
      fontSize: 10,
    }),
    valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
    menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
    option: (base, state) => ({
      ...base,
      padding: "6px 8px",
      backgroundColor: state.isSelected ? "#9481ce" : state.isFocused ? "#ede9fe" : "white",
      color: state.isSelected ? "white" : "#334155",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="relative flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-2xl bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {/* top header */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20"><FaTags className="text-sm" /></span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-[15px] font-semibold tracking-tight sm:text-lg">RFID Master</h2>
              <span className="text-[11px] font-semibold text-violet-100/90">{isFetching ? "refreshing..." : `(${totalDocs})`}</span>
            </div>
            <p className="hidden truncate text-[11px] text-violet-50 sm:block">Manage RFID codes, assignment and availability</p>
          </div>

          {isFetching && !isLoading && (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] sm:inline-flex">
              <Spinner size={10} />
              refreshing...
            </span>
          )}
        </div>

        <Link
          to="/organisation/rfid-master/create"
          className="inline-flex h-9 items-center gap-2 rounded-xl bg-white px-3 text-[11px] font-semibold text-[#6b4fd8] shadow-sm transition hover:bg-violet-50 sm:rounded-full"
        >
          <FaPlus className="text-[10px]" /><span className="hidden sm:inline">Add New</span><span className="sm:hidden">Add</span>
        </Link>
      </div>

      {/* filter bar */}
      <div className="shrink-0 border-b bg-slate-50/80 px-3 py-2 sm:px-4">
        <div className="flex items-end gap-2">
          {/* search */}
          <div className="flex min-w-[160px] flex-1 flex-col">
            <input
              type="text"
              placeholder="Search RFID code..."
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
              className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          {/* buttons */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openFilters}
              className="inline-flex h-9 items-center gap-1 rounded-lg border border-violet-100 bg-white px-3 text-[11px] font-semibold text-[#6b59c6] shadow-sm hover:bg-violet-50"
            >
              <FaFilter /> Filters
            </button>
          </div>
        </div>

        {/* active chips */}
        {(debouncedSearch || isActive !== "" || isAssigned !== "") && (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {debouncedSearch && (
              <span className="inline-flex items-center gap-2 text-[11px] bg-white border px-2 py-1 rounded-full shadow-sm">
                Search: {debouncedSearch}
                <button
                  className="text-slate-500 hover:text-red-500"
                  onClick={() => {
                    setSearchInput("");
                    setPage(1);
                  }}
                >
                  <FaTimesCircle />
                </button>
              </span>
            )}

            {isActive !== "" && (
              <span className="inline-flex items-center gap-2 text-[11px] bg-white border px-2 py-1 rounded-full shadow-sm">
                Active: {isActive === "true" ? "Yes" : "No"}
                <button
                  className="text-slate-500 hover:text-red-500"
                  onClick={() => {
                    setIsActive("");
                    setPage(1);
                  }}
                >
                  <FaTimesCircle />
                </button>
              </span>
            )}

            {isAssigned !== "" && (
              <span className="inline-flex items-center gap-2 text-[11px] bg-white border px-2 py-1 rounded-full shadow-sm">
                Assigned: {isAssigned === "true" ? "Yes" : "No"}
                <button
                  className="text-slate-500 hover:text-red-500"
                  onClick={() => {
                    setIsAssigned("");
                    setPage(1);
                  }}
                >
                  <FaTimesCircle />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* table */}
      <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain bg-white [-webkit-overflow-scrolling:touch]">
        {isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60">
            <div className="flex flex-col items-center gap-2 text-[#6b59c6] text-xs sm:text-sm">
              <Spinner size={26} />
              <span>Loading RFID master...</span>
            </div>
          </div>
        )}

        <table className="w-full min-w-[780px] text-left">
          <thead className="sticky top-0 z-40 bg-[#efeafd] text-[#4b3d91] shadow-sm">
            <tr>
              <th className={`${headerCell} w-[80px]`}>#</th>
              <th className={headerCell}>RFID Code</th>
              <th className={headerCell}>Active</th>
              <th className={headerCell}>Assigned</th>
              <th className={headerCell}>Created On</th>
              <th className={headerCell}>Updated On</th>
              <th className="sticky right-0 top-0 z-50 w-[82px] min-w-[82px] border-l border-violet-200 bg-[#ddd5f7] px-1 py-2 text-center text-[10px] font-semibold uppercase shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[104px] sm:min-w-[104px] sm:text-[11px]">
                <span className="sm:hidden">Act</span><span className="hidden sm:inline">Action</span>
              </th>
            </tr>
          </thead>

          <tbody className="bg-white">
            <AnimatePresence initial={false}>
              {isFetching && !isLoading ? (
                <SkeletonRows cols={7} rows={8} />
              ) : isError ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-sm text-red-500"
                  >
                    {error?.data?.message ||
                      error?.data?.msg ||
                      "Failed to load RFID master list"}
                  </td>
                </tr>
              ) : rows?.length > 0 ? (
                rows.map((item, index) => {
                  const id = getRowId(item);

                  return (
                    <motion.tr
                      key={id || index}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      className="group border-t border-slate-100 transition hover:bg-[#f7f5ff]"
                    >
                      <td className={dataCell}>
                        {(page - 1) * limit + index + 1}
                      </td>

                      <td className={`${dataCell} font-medium`}>
                        <div className="inline-block px-3 py-1 rounded-full border bg-white shadow-sm text-[11px]">
                          {getRfidCode(item)}
                        </div>
                      </td>

                      <td className={dataCell}>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[11px] ${
                            item?.isActive === true ||
                            item?.isActive === "true" ||
                            item?.status === true
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-rose-100 text-rose-700"
                          }`}
                        >
                          {item?.isActive === true ||
                          item?.isActive === "true" ||
                          item?.status === true
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </td>

                      <td className={dataCell}>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[11px] ${
                            item?.isAssigned === true ||
                            item?.isAssigned === "true"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {item?.isAssigned === true ||
                          item?.isAssigned === "true"
                            ? "Assigned"
                            : "Unassigned"}
                        </span>
                      </td>

                      <td className={dataCell}>
                        {formatDateTime(
                          item?.createdAt ||
                            item?.createdOn ||
                            item?.CreatedOn
                        )}
                      </td>

                      <td className={dataCell}>
                        {formatDateTime(
                          item?.updatedAt ||
                            item?.updatedOn ||
                            item?.lastUpdated ||
                            item?.UpdatedOn
                        )}
                      </td>

                      <td className="sticky right-0 z-10 w-[82px] min-w-[82px] whitespace-nowrap border-l border-violet-100 bg-violet-50 px-1 py-2 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[104px] sm:min-w-[104px]">
                        <div className="inline-flex items-center justify-center gap-0.5 sm:gap-1">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            type="button"
                            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-[#6b59c6] hover:bg-violet-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                            title="Edit"
                            onClick={() =>
                              navigate(`/settings/rfid-master/edit/${id}`)
                            }
                          >
                            <FaPencilAlt />
                          </motion.button>

                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            type="button"
                            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-rose-500 hover:bg-rose-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                            title="Delete"
                            onClick={() => openConfirmDelete(item)}
                          >
                            <FaTrashAlt />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-10 text-center text-sm text-slate-500"
                  >
                    No RFID master records found.
                  </td>
                </tr>
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* footer / pagination */}
      <div className="flex shrink-0 items-center justify-between gap-2 rounded-b-2xl border-t bg-slate-50/60 px-2 py-1.5 text-[10px] sm:px-4 sm:py-2 sm:text-sm">
        <div className="hidden text-slate-600 sm:block">
          Page <span className="font-semibold">{page}</span> of{" "}
          <span className="font-semibold">{Math.max(totalPages, 1)}</span> • {totalDocs} record(s)
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
          <div className="flex shrink-0 items-center gap-1 text-slate-600">
            <span className="hidden text-xs sm:inline">Rows / page</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((value) => ({ value, label: String(value) }))}
              onChange={(option) => { setLimit(Number(option?.value) || 10); setPage(1); }}
              isDisabled={isFetching || isLoading}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:flex-none sm:justify-start sm:gap-2">
            <button type="button" disabled={!hasPrev || isFetching || isLoading} onClick={() => setPage((prev) => Math.max(prev - 1, 1))} className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-violet-50 disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm" aria-label="Previous page">
              <span className="sm:hidden">&lsaquo;</span><span className="hidden sm:inline">Prev</span>
            </button>

            <span className="flex items-center gap-1 text-slate-600 sm:hidden">
              <Select
                value={{ value: totalPages ? page : 1, label: String(totalPages ? page : 1) }}
                options={Array.from({ length: Math.max(1, totalPages) }, (_, index) => ({ value: index + 1, label: String(index + 1) }))}
                onChange={(option) => setPage(Number(option?.value) || 1)}
                isDisabled={isFetching || isLoading}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[52px]"
                aria-label="Select page number"
              />
              <span>/ {Math.max(1, totalPages)}</span>
            </span>
            <span className="hidden items-center gap-1 text-slate-600 sm:flex">
              <span>Page</span>
              <Select value={{ value: totalPages ? page : 1, label: String(totalPages ? page : 1) }} options={Array.from({ length: Math.max(1, totalPages) }, (_, index) => ({ value: index + 1, label: String(index + 1) }))} onChange={(option) => setPage(Number(option?.value) || 1)} isDisabled={isFetching || isLoading} isSearchable={false} menuPlacement="top" menuPosition="fixed" menuPortalTarget={document.body} menuShouldScrollIntoView={false} styles={paginationSelectStyles} className="w-[58px]" aria-label="Select page number" />
              <span>of {Math.max(1, totalPages)}</span>
            </span>

            <button type="button" disabled={!hasNext || isFetching || isLoading} onClick={() => setPage((prev) => Math.min(prev + 1, Math.max(totalPages, 1)))} className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-violet-50 disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm" aria-label="Next page">
              <span className="sm:hidden">&rsaquo;</span><span className="hidden sm:inline">Next</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters modal */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsFilterOpen(false)}
          >
            <motion.div
              className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-xl"
              initial={{ scale: 0.94, y: 16, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.94, y: 16, opacity: 0 }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Filter RFID records</h3>
                  <p className="text-[11px] text-slate-500">Choose assignment and active status.</p>
                </div>
                <button type="button" onClick={() => setIsFilterOpen(false)} className="text-slate-500 hover:text-slate-700" aria-label="Close filters">
                  <FaTimesCircle />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-600">Active status</label>
                  <select value={tempFilters.isActive} onChange={(event) => setTempFilters((current) => ({ ...current, isActive: event.target.value }))} className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400">
                    <option value="">All</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-600">Assignment</label>
                  <select value={tempFilters.isAssigned} onChange={(event) => setTempFilters((current) => ({ ...current, isAssigned: event.target.value }))} className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400">
                    <option value="">All</option>
                    <option value="true">Assigned</option>
                    <option value="false">Unassigned</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-between border-t bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={() => {
                    handleReset();
                    setTempFilters({ isActive: "", isAssigned: "" });
                  }}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw /> Reset
                </button>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setIsFilterOpen(false)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] text-slate-700 hover:bg-slate-100">Close</button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsActive(tempFilters.isActive);
                      setIsAssigned(tempFilters.isAssigned);
                      setPage(1);
                      setIsFilterOpen(false);
                    }}
                    className="rounded-lg bg-[#9481ce] px-3 py-1.5 text-[11px] font-medium text-white hover:opacity-95"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* delete confirmation */}
      <ConfirmDialog
        open={confirm.open}
        title="Delete this RFID master?"
        description={`Are you sure you want to delete RFID ${
          confirm?.payload?.label || ""
        }?`}
        confirmLabel="Yes, delete"
        cancelLabel="Cancel"
        onClose={closeConfirm}
        onConfirm={handleConfirmDelete}
        loading={deletingRfid}
      />
    </div>
  );
};

export default RfidMasterList;
